<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col>
        <hm-spinner v-if="!ready" />
        <b-form @submit.prevent="onSubmit">
          <b-card header="Business Associate" v-show="ready">
            <Loading :active="busy" :is-full-page="false">
              <slot>
                <hm-spinner />
              </slot>
            </Loading>
            <hm-alert />
            <b-form-group label="Name of business associate (BA)" label-class="font-weight-bold">
              <b-form-input
                name="name"
                type="text"
                class="form-control"
                placeholder="Name"
                v-model="ba.name"
                v-validate="'required'"
                :disabled="busy"
                :class="{ 'is-invalid': errors.has('name') && submitted }"
              ></b-form-input>
            </b-form-group>
            <b-form-group label="Email" label-class="font-weight-bold">
              <b-form-input
                name="email"
                type="text"
                class="form-control"
                placeholder="Email"
                autocomplete="email"
                v-model="ba.email"
                v-validate="'required|email'"
                :disabled="busy"
                :class="{ 'is-invalid': errors.has('email') && submitted }"
              ></b-form-input>
            </b-form-group>
            <b-form-group label="Phone" label-class="font-weight-bold">
              <the-mask
                name="phone"
                :mask="'(###) ###-####'"
                type="text"
                class="form-control"
                placeholder="(xxx) xxx-xxxx"
                autocomplete="phone"
                v-model="ba.phone"
                v-validate="'required|numeric|length:10'"
                :disabled="busy"
                :class="{ 'is-invalid': errors.has('phone') && submitted }"
              ></the-mask>
            </b-form-group>
            <b-form-group>
              <div slot="label">
                <strong>What access to practice facilities, information systems, electronic devices, and ePHI does the BA require?</strong>
                <br />Ensure the practice only enables access to ePHI by determining the least access that is necessary for this service provider (BA) to perform the roles and responsibilities assigned. Examples of least privileges and minimum necessary access questions are:
                <ul>
                  <li>What facilities need to be accessed and at what times?</li>
                  <li>What information systems need to be accessed and at what times?</li>
                  <li>Is remote access to information systems necessary and appropriate?</li>
                  <li>Is access from an electronic device (laptop, tablet, smart phone and the like) necessary and appropriate?</li>
                  <li>Under what circumstances must access be supervised?</li>
                </ul>
              </div>
              <b-form-textarea
                no-resize
                :rows="5"
                name="access"
                class="form-control"
                v-model="ba.access"
                v-validate="'required'"
                :disabled="busy"
                :class="{ 'is-invalid': errors.has('access') && submitted }"
              />
            </b-form-group>
            <b-form-group label="Does the BA require physical access to the practice?" label-class="font-weight-bold">
              <hm-switch class="mx-1" v-model="ba.physicalAccess" data-type="yesno" :disabled="busy" />
            </b-form-group>
            <b-form-group>
              <div slot="label">
                <strong>Has the BA signed a Business Associate Agreement?</strong>
                <br />A business associate is permitted to create, receive, maintain, or transmit electronic protected health information on the practice's behalf only after satisfactory assurances are obtained, in writing, that the business associate
                will appropriately safeguard the information. A signed agreement is the instrument through which a business associate can provide &quot;satisfactory assurances.&quot;
              </div>
              <hm-switch class="mx-1" v-model="ba.signed" data-type="yesno" :disabled="busy" />
              <hm-note>
                <u>Note</u>: Business associates with hundreds of customers typically have a pre-signed BA agreement as part of their terms of service. Meaning, they don't physically sign a BA agreement with each individual customer&#59;
                instead, they post a &quot;pre-signed&quot; agreement online for everyone. If this is true, select
                <i>YES</i> above. For good measure, copy the link to the actual agreement in the text area above, or at least make mention
                the BA provides a pre-signed agreement to all customers.
              </hm-note>
            </b-form-group>

            <div slot="footer">
              <b-row>
                <b-col cols="6">
                  <b-button type="button" variant="secondary" class="px-4" :disabled="busy" to="/ba-list">Back</b-button>
                  <b-button type="button" variant="danger" class="px-4 ml-2" :disabled="busy" v-show="id" @click="dlgDelete = true">Delete</b-button>
                </b-col>
                <b-col cols="6" class="text-right">
                  <b-button type="submit" variant="primary" class="px-4" :disabled="busy">Save</b-button>
                </b-col>
              </b-row>
            </div>
          </b-card>
        </b-form>
      </b-col>
    </b-row>

    <!-- Signed Agreement Modal Dialog -->
    <b-modal id="dlgSigned" class="modal-primary" hide-header-close v-model="dlgSigned">
      <div class="font-weight-bold" slot="modal-title">
        <i class="fa fas fa-edit pr-2"></i>Signed BA Agreement Needed
      </div>
      <p>Okay, you don't have a signed agreement with this business associate. HIPAAMATE will remind you during the Risk Management stage to obtain one.</p>
      <p>However, it is very important to obtain a signed agreement as soon as possible!</p>
      <div slot="modal-footer">
        <b-button variant="primary" class="ml-2" @click="dlgSigned = false; onSave()">OK</b-button>
      </div>
    </b-modal>

    <!-- Delete Modal Dialog -->
    <b-modal id="dlgDelete" class="modal-primary" hide-header-close v-model="dlgDelete">
      <div class="font-weight-bold" slot="modal-title">
        <i class="fa fas fa-trash pr-2"></i>Delete Confirmation
      </div>
      <p>Are you sure you want to remove this business associate?</p>
      <div slot="modal-footer">
        <b-button variant="default" @click="dlgDelete = false">No</b-button>
        <b-button variant="primary" class="ml-2" @click="dlgDelete = false; onDelete()">Yes</b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { practiceService } from '@/services'

export default {
  name: 'BAEdit',
  props: ['id'],
  data() {
    return {
      ba: {
        id: '',
        name: '',
        email: '',
        phone: '',
        access: '',
        physicalAccess: false,
        signed: false
      },
      dlgSigned: false,
      dlgDelete: false,
      ready: false,
      busy: false,
      submitted: false
    }
  },
  methods: {
    ...mapActions({
      alertClear: 'alert/clear',
      alertError: 'alert/error',
      alertException: 'alert/exception',
      setProgress: 'progress/setFlagDone'
    }),
    onSubmit() {
      this.busy = true;
      this.submitted = true;
      this.alertClear();

      this.$validator.validate()
        .then(valid => {
          if (valid) {
            if (!this.ba.signed) {
              this.dlgSigned = true;
            } else {
              return this.onSave();
            }
          } else {
            this.alertError('Please complete business associate information and submit again.');
          }
        })
        .finally(() => {
          this.busy = false;
        });
    },
    onSave() {
      this.busy = true;

      return practiceService.setBA(this.ba)
        .then(data => {
          this.setProgress('BASet');
          this.$router.push('/ba-list');
        })
        .catch(err => {
          this.alertException({ err, message: 'Error saving business associate.' });
        })
        .finally(() => {
          this.busy = false;
        });
    },
    onDelete() {
      this.busy = true;
      this.submitted = true;
      this.alertClear();

      return practiceService.deleteBA(this.ba)
        .then(data => {
          this.$router.push('/ba-list');
        })
        .catch(err => {
          this.alertException({ err, message: 'Error deleting business associate.' });
        })
        .finally(() => {
          this.busy = false;
        });
    },
    onLoad() {
      this.ready = false;

      if (this.id) {
        practiceService.getBA(this.id)
          .then(data => {
            this.ba = data;
          })
          .catch(err => {
            this.alertException({ err, message: 'Error loading business associate.' });
          }).finally(() => {
            this.ready = true;
          });
      } else {
        this.ready = true;
      }
    }
  },
  created() {
    return this.onLoad();
  },
  watch: { // you have to use function syntax here, as warned in the docs
    '$route.params.id': function (newVal, oldVal) {
      this.onLoad();
    }
  }
}
</script>
